import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import config from "src/config";
import { useClientHeight } from "@/hooks/useClientHeight";
import { api } from "@/services/api";
import { selectAppointmentIsAppointment, selectAppointmentUserIsDoctor } from "@/store/features/appointment/selectors";
import { showUIAlert } from "@/store/features/ui/slice";
import { AlertTypes } from "@/store/features/ui/types";
import { selectUserIsAdmin } from "@/store/features/user/selectors";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { Button, Result, Checkbox } from "@/ui";
import { tokenStorage } from "@/utils/storage";
import { showErrorToast } from "@/utils/toast";
import { StyledCallEnded, FeedbackContainer, FeedbackOptions, FeedbackTextArea, FeedbackButtons } from "./styled";

enum ReasonValuesEnum {
  technical,
  doctor,
  translation
}

const CallEnded: FC = () => {
  const height = useClientHeight();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const {id} = useParams();

  const isLoggedIn = !!tokenStorage.getItem();
  const isAppointment = useAppSelector(selectAppointmentIsAppointment);
  const isDoctor = useAppSelector(selectAppointmentUserIsDoctor);
  const isAdmin = useAppSelector(selectUserIsAdmin);

  const url = useMemo(() => {
    if (isAdmin) {
      // TODO: add env
      return config.doctor_url.replace('doctor', 'admin');
    }

    if (isDoctor) {
      return `${config.doctor_url}/appointments/details/${id}`;
    }

    return config.patient_url;
  }, [id, isAdmin, isDoctor]);

  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState<(string | number)[]>([]);
  const [satisfied, setSatisfied] = useState("");
  const [description, setDescription] = useState("");

  const onClickSatisfied = async () => {
    try {
      setSatisfied("yes");
      setLoading(true);

      await api.post('/appointments/call-reviews', {
        appointment_id: id,
        satisfied: true
      });

      dispatch(
        showUIAlert({
          title: t('feedback_thank_you'),
          message: t('feedback_appreciation'),
          url,
          type: AlertTypes.success,
        }),
      );
    } catch {
      showErrorToast(t('error'), t('something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      await api.post('/appointments/call-reviews', {
        appointment_id: id,
        review: description || " ",
        issues,
        satisfied: false
      });

      setSatisfied("");

      dispatch(
        showUIAlert({
          title: t('feedback_thank_you'),
          message: t('feedback_appreciation'),
          url,
          type: AlertTypes.success,
        }),
      );
    } catch {
      showErrorToast(t('error'), t('something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn || !isAppointment || isAdmin) {
    return (
      <StyledCallEnded $height={height}>
        <Result
          title={t('call_ended')}
          message={t('meeting_ended_message')}
          image="success.svg"
        >
          <p>{t('not_finished_message')} <Link to={`/home/${id}`}>{t('rejoin_call')}</Link></p>
          <a href={url}>
            <Button size="medium">{t('return_to_home')}</Button>
          </a>
        </Result>
      </StyledCallEnded>
    );
  }

  return (
    <StyledCallEnded $height={height}>
      <Result
        title={t("consultation_complete")}
        message={t("consultation_ended_message")}
        image="success.svg"
      >
        <p>{t('not_finished_message')} <Link to={`/home/${id}`}>{t('rejoin_call')}</Link></p>
        <FeedbackContainer>
          <FeedbackButtons>
            <Button
              disabled={loading}
              variant="secondary"
              onClick={onClickSatisfied}
            >
              {t("feedback_yes")}
            </Button>
            <Button
              disabled={loading}
              variant={satisfied === "no" ? "primary" : "secondary"}
              onClick={() => setSatisfied("no")}
            >
              {t("feedback_no")}
            </Button>
          </FeedbackButtons>

          {satisfied === "no" && (
            <>
              <FeedbackOptions>
                <Checkbox.Group value={issues} onChange={setIssues}>
                  <Checkbox value={ReasonValuesEnum.technical}>{t("feedback_issue_technical")}</Checkbox>
                  <Checkbox value={ReasonValuesEnum.translation}>{t("feedback_issue_translator")}</Checkbox>
                  {!isDoctor && <Checkbox value={ReasonValuesEnum.doctor}>{t("feedback_issue_doctor")}</Checkbox>}
                </Checkbox.Group>
              </FeedbackOptions>
              <FeedbackTextArea
                rows={3}
                value={description}
                placeholder={t("feedback_description_placeholder")}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Button size="medium" onClick={handleSubmit} disabled={loading}>
                {t("submit_feedback")}
              </Button>
            </>
          )}
        </FeedbackContainer>
      </Result>
    </StyledCallEnded>
  );
};

export default CallEnded;
